import { z } from 'zod'

import type { MapItem } from 'src/shared/schemas/utils'

import type { LoanPurpose } from './loan-purpose'

export type PurchaseLoanType =
  | 'conventional'
  | 'fha'
  | 'va'
  | 'usda'
  | 'jumbo'
  | 'bridge'
  | 'construction_to_permanent'
  | 'commercial'
  | 'other'

export type RefinanceLoanType =
  | 'rate_and_term_refinance'
  | 'cash_out_refinance'
  | 'streamline_refinance'
  | 'debt_consolidation_loan'
  | 'commercial_loan'
  | 'other'

export type OtherLoanType =
  | 'hel'
  | 'heloc'
  | 'reverse_mortgage'
  | 'renovation_loan'
  | 'construction_loan'
  | 'second_mortgage'
  | 'business_purpose_loan'
  | 'hard_money_loan'
  | 'commercial'
  | 'other'

export type LoanType = PurchaseLoanType | RefinanceLoanType | OtherLoanType

export const loanTypeMap: {
  purchase: Record<PurchaseLoanType, MapItem>
  refinance: Record<RefinanceLoanType, MapItem>
  other: Record<OtherLoanType, MapItem>
} = {
  purchase: {
    conventional: { label: 'Conventional' },
    fha: { label: 'FHA' },
    va: { label: 'VA' },
    usda: { label: 'USDA' },
    jumbo: { label: 'Jumbo' },
    bridge: { label: 'Bridge' },
    construction_to_permanent: { label: 'Construction-to-Permanent' },
    commercial: { label: 'Commercial' },
    other: { label: 'Other' },
  },
  refinance: {
    rate_and_term_refinance: { label: 'Rate-and-Term Refinance' },
    cash_out_refinance: { label: 'Cash-Out Refinance' },
    streamline_refinance: { label: 'Streamline Refinance' },
    debt_consolidation_loan: { label: 'Debt Consolidation Loans' },
    commercial_loan: { label: 'Commercial' },
    other: { label: 'Other' },
  },
  other: {
    hel: { label: 'Home Equity Loans (HELs)' },
    heloc: { label: 'Home Equity Lines of Credit (HELOCs)' },
    reverse_mortgage: { label: 'Reverse Mortgages' },
    renovation_loan: { label: 'Renovation Loans' },
    construction_loan: { label: 'Construction Loans' },
    second_mortgage: { label: 'Second Mortgages' },
    business_purpose_loan: { label: 'Business Purpose Loans' },
    hard_money_loan: { label: 'Hard Money Loans' },
    commercial: { label: 'Commercial' },
    other: { label: 'Other' },
  },
}

export const flatLoanTypeMap = {
  conventional: { label: 'Conventional' },
  fha: { label: 'FHA' },
  va: { label: 'VA' },
  usda: { label: 'USDA' },
  jumbo: { label: 'Jumbo' },
  bridge: { label: 'Bridge' },
  construction_to_permanent: { label: 'Construction-to-Permanent' },
  commercial: { label: 'Commercial' },
  other: { label: 'Other' },
  rate_and_term_refinance: { label: 'Rate-and-Term Refinance' },
  cash_out_refinance: { label: 'Cash-Out Refinance' },
  streamline_refinance: { label: 'Streamline Refinance' },
  debt_consolidation_loan: { label: 'Debt Consolidation Loans' },
  commercial_loan: { label: 'Commercial' },
  hel: { label: 'Home Equity Loans (HELs)' },
  heloc: { label: 'Home Equity Lines of Credit (HELOCs)' },
  reverse_mortgage: { label: 'Reverse Mortgages' },
  renovation_loan: { label: 'Renovation Loans' },
  construction_loan: { label: 'Construction Loans' },
  second_mortgage: { label: 'Second Mortgages' },
  business_purpose_loan: { label: 'Business Purpose Loans' },
  hard_money_loan: { label: 'Hard Money Loans' },
}

export const getLoanTypeSchema = (purpose: LoanPurpose) => {
  const validTypes = Object.keys(loanTypeMap[purpose])
  return z.enum(validTypes as [LoanType, ...LoanType[]])
}

export const loanTypeSchema = z.union([
  getLoanTypeSchema('purchase'),
  getLoanTypeSchema('refinance'),
  getLoanTypeSchema('other'),
])
